import { useState, useRef, useEffect } from "react";
import {
  Copy,
  Edit,
  Volume2,
  Download,
  Pause,
  Play,
  Save,
  X,
  Mic,
  StopCircle,
} from "lucide-react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import useUserStore from "../../store/useUserStore";
import MicRecorder from "mic-recorder-to-mp3";
import NoSleep from "nosleep.js";
import "./TranscriptView.css";
import { data } from "react-router-dom";
import usePatientStore from "../../store/usePatientStore";
import CustomPopup from "../CustomPopup";
import { CircularProgress } from "@mui/material";
import useSurgicalStore from "../../store/useSurgicalStore";

const Mp3Recorder = new MicRecorder({
  bitRate: 64,
  prefix: "data:audio/wav;base64,",
});

const TranscriptView = ({ transcript, audioUrl, consultationId,isSurgical=false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTranscript, setEditedTranscript] = useState(transcript);
  const [isAudioFile, setIsAudioFile] = useState(null);
  const { isMobile, setIsRecordingStarted ,fetchSessions,fetchSurgicalData,user} = useUserStore();
  const { getConsultationDetail } = usePatientStore();
  const {getSurgicalDetail} = useSurgicalStore();
  const [showSaveReminder, setShowSaveReminder] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingBlob, setRecordingBlob] = useState(null);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [isLoading, setisLoading] = useState(false);
  const [time, setTime] = useState(0);
  const timerRef = useRef(null);
  const noSleep = useRef(new NoSleep());

  useEffect(() => {
    setIsAudioFile(null);
  }, [audioUrl]);

  const handleTimeUpdate = () => {
    if (audioUrl) setCurrentTime(audioRef.current.currentTime);
  };

  const startRecording = () => {
    setTime(0);
    Mp3Recorder.start()
      .then(() => {
        setIsRecordingStarted(true);
        setIsRecording(true);
        noSleep.current.enable();
        timerRef.current = setInterval(() => {
          setTime((prev) => {
            // recordingTimeRef.current = prev + 1;
            return prev + 1;
          });
        }, 1000);
      })
      .catch((e) => console.error(e));
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    Mp3Recorder.stop()
      .getMp3()
      .then(([buffer, blob]) => {
        setIsRecordingStarted(false);
        setIsRecording(false);
        setRecordingBlob(blob);
        setRecordingUrl(URL.createObjectURL(blob));
        noSleep.current.disable();
        setShowSaveReminder(true);
      })
      .catch((e) => console.error("Error in recording:", e));
  };

  const closeSaveReminder = () => {
    setShowSaveReminder(false);
  };

  const handleLoadedMetadata = () => {
    console.log("Metadata Loaded. Duration:", audioRef.current.duration);
    if (audioRef?.current?.duration) {
      setIsAudioFile(true);
      setDuration(audioRef?.current?.duration);
    }
  };

  const togglePlay = () => {
    if (audioUrl && isAudioFile) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }

      setIsPlaying(!isPlaying);
    } else {
      alert("No Audio File");
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(transcript);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedTranscript(transcript);
  };

  const handleSave = async () => {
    if (editedTranscript === transcript && !recordingBlob) {
      handleCancel();
      return;
    }
    noSleep.current.enable()
    console.log(recordingBlob);
    setIsEditing(false);
    setisLoading(true)
    let data;
    if (recordingBlob) {
      const fName = `recording_${new Date().toISOString()}.mp3`;
      const formData = new FormData();
      formData.append(
        "file",
        new File([recordingBlob], fName, { type: "audio/mp3" })
      );

      formData.append("consultation_id", consultationId);

      try {
        data = await uploadRecording(formData);
        console.log("Recording uploaded successfully", data.blob_name);
      } catch (error) {
        console.error("Error uploading recording:", error);
        throw error;
      }
    }

    const filename = data?.blob_name || "";
    console.log(filename);

    try {
      console.log("++++++++++");
      await updateTranscript(filename, consultationId, editedTranscript);
      setIsEditing(false);
      setRecordingBlob(null);
      if(isSurgical){
        await fetchSurgicalData(user?.dentist_id)
        await getSurgicalDetail(consultationId)
      }
      else{
        await fetchSessions(user?.dentist_id)
        await getConsultationDetail(consultationId);
      }
    } catch (error) {
      console.error("Error saving transcript:", error);
    }

    setisLoading(false)
    noSleep.current.disable()
  };

  const handleCancel = () => {
    Mp3Recorder.stop();
    clearInterval(timerRef.current);
    setTime(0);
    setIsRecordingStarted(false);
    setIsRecording(false);
    setIsEditing(false);
    setRecordingBlob(null);
    setEditedTranscript(transcript); // Reset to original transcript
  };

  const handleDownload = () => {
    if (audioUrl && isAudioFile) {
      const link = document.createElement("a");
      link.href = audioUrl;
      link.download = `audio_${Date.now()}.mp3`; // You can customize the file name and extension
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const uploadRecording = async (formData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/recordings/upload`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error uploading recording:", error);
      throw error;
    }
  };

  async function updateTranscript(filename, consultationId, textContent) {
    const formData = new FormData();

    if (filename) {
      formData.append("filename", filename);
    }

    const textBlob = new Blob([textContent], { type: "text/plain" });
    formData.append("file", textBlob, "transcript.txt");

    let url;
    if(isSurgical){
      formData.append("surgical_id", consultationId);
      url=`${process.env.REACT_APP_SUMMARY_SERVER_URL}/update-surgical-transcript/`
    }
    else{
      formData.append("consultation_id", consultationId);
      url=`${process.env.REACT_APP_SUMMARY_SERVER_URL}/update-transcript/`
    }

    try {
      const response = await fetch(
        url,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            // Content-Type is automatically set for FormData
          },
          body: formData,
        }
      );

      const result = await response.json();
      console.log("API Response:", result);
      return result;
    } catch (error) {
      console.error("Error calling API:", error);
      return null;
    }
  }

  return (
    <div className="transcript-container">
      {!isLoading && (
        <div className="transcript-header">
          <div className="transcript-actions">
            {isEditing ? (
              <>
                <button
                  className="transcript-action-button"
                  onClick={handleCancel}
                >
                  <X size={isMobile ? 14 : 18} />
                  Cancel
                </button>
                {isRecording ? (
                  <button
                    className="transcript-action-button recording"
                    onClick={stopRecording}
                  >
                    <StopCircle size={isMobile ? 14 : 18} />
                    Stop {formatTime(time)}
                  </button>
                ) : (
                  <button
                    className="transcript-action-button"
                    onClick={startRecording}
                  >
                    <Mic size={isMobile ? 14 : 18} />
                    Edit via Voice
                  </button>
                )}
                <button
                  className="transcript-action-button save-button"
                  onClick={handleSave}
                  disabled={isRecording}
                >
                  <Save size={isMobile ? 14 : 18} />
                  Save
                </button>
              </>
            ) : (
              <>
                <button
                  className="transcript-action-button"
                  onClick={handleCopy}
                >
                  <Copy size={isMobile ? 14 : 18} />
                  Copy
                </button>
                <button
                  className="transcript-action-button"
                  onClick={handleEdit}
                >
                  <Edit size={isMobile ? 14 : 18} />
                  Edit via Text/Voice
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {!isLoading && (
        <div className="transcript-content">
          {isEditing ? (
            <textarea
              value={editedTranscript}
              onChange={(e) => setEditedTranscript(e.target.value)}
              className="transcript-editor"
            />
          ) : (
            <Markdown
              remarkPlugins={[remarkGfm]}
              className="transcript-markdown-content"
            >
              {transcript}
            </Markdown>
          )}
        </div>
      )}

      {/* Render audio player only if it's not a .txt file */}
      {audioUrl && !isLoading && !audioUrl.endsWith(".txt") && (
        <div className="audio-player">
          <div className="audio-info">
            <span className="recording-name">Recording 600.mp3</span>
            <span className="time-display">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          <div className="audio-controls">
            <button className="play-button" onClick={togglePlay}>
              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
            </button>

            <div className="progress-bar-container">
              <input
                type="range"
                min="0"
                max={duration}
                value={currentTime}
                className="progress-bar"
                onChange={(e) => {
                  const time = Number.parseFloat(e.target.value);
                  setCurrentTime(time);
                  audioRef.current.currentTime = time;
                }}
              />
            </div>

            <div className="audio-actions">
              <button className="audio-action-button">
                <Volume2 size={20} />
              </button>
              <button className="audio-action-button" onClick={handleDownload}>
                <Download size={20} />
              </button>
            </div>
          </div>
          <audio
            ref={audioRef}
            src={audioUrl}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={() => setIsPlaying(false)}
          />
        </div>
      )}
      {isLoading && (
        <CircularProgress
          size={60}
          sx={{ position: "relative", top: "35%", left: "40%" }}
        />
      )}
      <CustomPopup isOpen={showSaveReminder} onClose={closeSaveReminder} />
    </div>
  );
};

export default TranscriptView;
